import { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';

import { useRoomContext } from '@src/contexts';
import { useWebinarRoomContext } from '@src/contexts/webinar';

import {
  useNotificationStore,
  usePeersStore,
  useQuestionAndAnswerStore,
  useRoomStore,
  useVideoStreamStore,
  useWebinarRoomStore,
} from '@src/stores';

import useCommonSocketListener from './useCommonSocketListener';
import { useWebinarRecording } from '../webinar';

import { handleEndSessionEvent, openURL } from '@src/utils';
import { queryKeys } from '@src/utils/queryKeys';

import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge';

import { EBrowserEvents, INFO_MODAL_ICONS, EWebinarSockets } from '@src/constants';

import {
  EAnswerType,
  EQuestionAndAnswerSockets,
  EUserType,
  EWebinarEventsTypes,
  IQuestion,
  IUpdatePeerInfoEvent,
  IWebinarEvent,
} from '@src/types';

const useWebinarSocketListener = () => {
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { handleUpdatePeerInfo, currentPeer } = useRoomContext();
  const { leaveChannel } = useWebinarRoomContext();
  const { saveRecording } = useWebinarRecording();

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  useCommonSocketListener({ isConnected });

  useEffect(() => {
    const timer = setInterval(() => {
      const isSocketConnected = SocketService.getSocketConnectionState();
      if (isSocketConnected) {
        setIsConnected(isSocketConnected);
        clearInterval(timer);
      }
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!isConnected) return;

    const onEndSession = async (data: IWebinarEvent) => {
      await leaveChannel?.();
      await handleEndSessionEvent(data, navigate);
    };

    const handleWebinarEvent = async (data: IWebinarEvent) => {
      switch (data.eventType) {
        case EWebinarEventsTypes.END_SESSION:
          await onEndSession(data);
          break;
        case EWebinarEventsTypes.HOST_JOIN:
          useWebinarRoomStore.getState().setIsWebinarStartedByHost(true);
          break;
        default:
          console.warn(`Unhandled webinar event type: ${data.eventType}`);
      }
    };

    SocketService.on(EWebinarSockets.EVENT, handleWebinarEvent);

    return () => {
      SocketService.off(EWebinarSockets.EVENT, handleWebinarEvent);
    };
  }, [isConnected, leaveChannel, navigate]);

  useEffect(() => {
    if (!isConnected) return;

    const handlePeerInfo = (data: IUpdatePeerInfoEvent) => {
      const { peer_id, peer_name, type, status, extraData } = data;
      handleUpdatePeerInfo({ peer_name, peer_id, type, status, emit: false, extraData });
    };

    SocketService.on(EWebinarSockets.UPDATE_PEER_INFO, handlePeerInfo);

    return () => {
      SocketService.off(EWebinarSockets.UPDATE_PEER_INFO, handlePeerInfo);
    };
  }, [handleUpdatePeerInfo, isConnected, navigate]);

  useEffect(() => {
    if (!isConnected) return;

    const handleViewersCount = ({ viewersCount }: { viewersCount: number }) => {
      usePeersStore.getState().setViewersCount(viewersCount);
    };

    const handleViewerIframeLayout = ({
      layout,
      pinUserIds,
      unpinUserIds,
    }: {
      layout: string;
      pinUserIds?: number[];
      unpinUserIds?: number[];
    }) => {
      useWebinarRoomStore.getState().handleViewerIframeLayoutUpdate(layout, pinUserIds, unpinUserIds);
    };

    SocketService.on(EWebinarSockets.VIEWERS, handleViewersCount);
    SocketService.on(EWebinarSockets.VIEWER_IFRAME_LAYOUT, handleViewerIframeLayout);

    return () => {
      SocketService.off(EWebinarSockets.VIEWERS, handleViewersCount);
      SocketService.off(EWebinarSockets.VIEWER_IFRAME_LAYOUT, handleViewerIframeLayout);
    };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleSubmitQuestion = (data: { question: IQuestion }) => {
      const { question } = data;
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });

      appendNotification({
        title: 'Incoming Question',
        text: ` ${question.creator.name} Asked: ${question.text}`,
        source: 'question-and-answer',
      });
    };

    const handleEditQuestion = () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });
    };

    const handleAnswerQuestion = (data: { question: IQuestion }) => {
      const { answer, creator } = data.question || {};

      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });

      switch (answer?.answerType) {
        case EAnswerType.PUBLIC:
          if (currentPeer?.id === creator?.id) {
            appendNotification({
              title: 'You’ve Got an Answer',
              text: 'Your question is shared with everyone by webinar admin.',
              source: 'question-and-answer',
            });
          } else {
            appendNotification({
              title: 'Shared Question',
              text: 'A new question has been shared in Q&A.',
              source: 'question-and-answer',
            });
          }
          break;
        case EAnswerType.PRIVATE:
          if (currentPeer?.id === creator?.id) {
            appendNotification({
              title: 'You’ve Got an Answer',
              text: 'An answer is ready for your question. Open Q&A to view it.',
              source: 'question-and-answer',
            });
          }
          break;
        case EAnswerType.LIVE:
          if (currentPeer?.id === creator?.id) {
            appendNotification({
              title: 'You’re Question Being Answered',
              text: 'Your question is being answered live',
              source: 'question-and-answer',
            });
          }
          break;
        default:
          console.warn(`Unhandled answer type: ${answer?.answerType}`);
      }
    };

    const handleDismissQuestion = (data: { questionId: string }) => {
      const { questionId } = data || {};

      if (!questionId) {
        console.error('DISMISS_QUESTION missing questionId', data);
        return;
      }

      // TODO: this will be removed when dismissed question being returned in my questions
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.myQuestions],
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });

      const myQuestionsList = useQuestionAndAnswerStore.getState().myQuestionsList;

      if (myQuestionsList.includes(questionId)) {
        appendNotification({
          title: 'Question Dismissed',
          text: 'Your submitted question was dismissed.',
          source: 'question-and-answer',
        });
      }
    };

    const handleMarkQuestionAsAnswered = (data: { question: IQuestion }) => {
      const { creator } = data.question || {};

      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });

      if (currentPeer?.record_id === creator?.recordId) {
        appendNotification({
          title: 'You’ve Got an Answer',
          text: 'Your question is being answered live.',
          source: 'question-and-answer',
        });
      }
    };
    const handleReplayAnswer = (data: { question: IQuestion }) => {
      const { creator, assignTo } = data.question || {};
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questionReply(data.question.id)],
      });

      const isAssignedToQuestion =
        currentPeer?.record_id === assignTo?.recordId && assignTo?.recordId !== EUserType.HOST;

      if (currentPeer?.id === creator?.id || isAssignedToQuestion) {
        appendNotification({
          title: 'New Host Response',
          text: `The host added a reply to ${isAssignedToQuestion ? 'a question you answered' : 'your question'}.`,
          source: 'question-and-answer',
        });
      }
    };

    SocketService.on(EQuestionAndAnswerSockets.ANSWER_QUESTION, handleAnswerQuestion);
    SocketService.on(EQuestionAndAnswerSockets.REPLY_TO_ANSWER, handleReplayAnswer);
    SocketService.on(EQuestionAndAnswerSockets.SUBMIT_QUESTION, handleSubmitQuestion);
    SocketService.on(EQuestionAndAnswerSockets.EDIT_QUESTION, handleEditQuestion);
    SocketService.on(EQuestionAndAnswerSockets.DISMISS_QUESTION, handleDismissQuestion);
    SocketService.on(EQuestionAndAnswerSockets.MARK_QUESTION_AS_ANSWERED, handleMarkQuestionAsAnswered);

    return () => {
      SocketService.off(EQuestionAndAnswerSockets.SUBMIT_QUESTION, handleSubmitQuestion);
      SocketService.off(EQuestionAndAnswerSockets.EDIT_QUESTION, handleEditQuestion);
      SocketService.off(EQuestionAndAnswerSockets.REPLY_TO_ANSWER, handleReplayAnswer);
      SocketService.off(EQuestionAndAnswerSockets.ANSWER_QUESTION, handleAnswerQuestion);
      SocketService.off(EQuestionAndAnswerSockets.DISMISS_QUESTION, handleDismissQuestion);
      SocketService.off(EQuestionAndAnswerSockets.MARK_QUESTION_AS_ANSWERED, handleMarkQuestionAsAnswered);
    };
  }, [appendNotification, currentPeer, isConnected, queryClient]);

  useEffect(() => {
    if (!isConnected) return;

    const handleEditAnswer = (data: { question: IQuestion }) => {
      const question = data?.question;
      if (!question?.id) return;

      useQuestionAndAnswerStore.getState().updateQuestion(question.id, question);
    };

    SocketService.on(EQuestionAndAnswerSockets.EDIT_ANSWER, handleEditAnswer);

    return () => {
      SocketService.off(EQuestionAndAnswerSockets.EDIT_ANSWER, handleEditAnswer);
    };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleDeleteQuestion = () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });
    };

    SocketService.on(EQuestionAndAnswerSockets.DELETE_QUESTION, handleDeleteQuestion);

    return () => {
      SocketService.off(EQuestionAndAnswerSockets.DELETE_QUESTION, handleDeleteQuestion);
    };
  }, [appendNotification, isConnected, queryClient]);

  useEffect(() => {
    if (!isConnected) return;

    const handleAssignQuestion = (data: { question: IQuestion }) => {
      const { assignTo, assignedBy } = data.question || {};
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });

      if (currentPeer?.record_id === assignTo?.recordId && assignedBy?.recordId !== currentPeer?.record_id) {
        appendNotification({
          title: 'New Question Assigned',
          text: 'A new question has been assigned to you.',
          source: 'question-and-answer',
        });
      }
    };

    SocketService.on(EQuestionAndAnswerSockets.ASSIGN_QUESTION, handleAssignQuestion);

    return () => {
      SocketService.off(EQuestionAndAnswerSockets.ASSIGN_QUESTION, handleAssignQuestion);
    };
  }, [appendNotification, currentPeer, currentPeer?.id, isConnected, queryClient]);

  useEffect(() => {
    if (!isConnected) return;

    const handleUnassignQuestion = (data: { question: IQuestion }) => {
      const question = data?.question;
      if (!question?.id) return;

      useQuestionAndAnswerStore.getState().updateQuestion(question.id, question);
    };

    //only hosts and cohosts receive this event when one of them closes the answer box before answering(unassigning the question)
    SocketService.on(EQuestionAndAnswerSockets.UNASSIGN_QUESTION, handleUnassignQuestion);

    return () => {
      SocketService.off(EQuestionAndAnswerSockets.UNASSIGN_QUESTION, handleUnassignQuestion);
    };
  }, [isConnected, queryClient]);

  useEffect(() => {
    if (!isConnected) return;

    const handleVoiceReporter = (data: { isReporter: boolean }) => {
      const { setReporter } = useVideoStreamStore.getState();

      setReporter(data.isReporter);
    };

    SocketService.on(EWebinarSockets.VOICE_REPORTER_UPDATE, handleVoiceReporter);

    return () => {
      SocketService.off(EWebinarSockets.VOICE_REPORTER_UPDATE, handleVoiceReporter);
    };
  }, [isConnected]);

  const showConnectionLossModal = useCallback(() => {
    const { isInfoModalOpen, setInfoModalContent, setIsInfoModalOpen } = useRoomStore.getState();

    if (isInfoModalOpen) return;

    setInfoModalContent({
      icon: INFO_MODAL_ICONS.WARNING,
      title: 'Oops! Something Went Wrong',
      description: 'Please try again later',
      errorPriority: 'HIGH',
      buttons: [
        {
          text: 'Got it',
          onClick: () => {
            sendEventToWebView({ eventType: EWebViewEvents.WEB_SHEET_DISMISSED });
            openURL('/webinar/rejoin');
          },
          variant: 'primary',
        },
      ],
      handleOnCloseModal: () => {
        openURL('/webinar/rejoin');
      },
    });
    setIsInfoModalOpen(true);
  }, []);

  // Handles both socket-level and browser-level disconnection events
  const handleConnectionLoss = useCallback(async () => {
    saveRecording('Socket disconnected');
    await leaveChannel?.();
    showConnectionLossModal();
  }, [leaveChannel, saveRecording, showConnectionLossModal]);

  useEffect(() => {
    // Listen for socket disconnection (server crash, restart, etc.)
    SocketService.on(EWebinarSockets.DISCONNECTING, handleConnectionLoss);
    // Listen for browser-level network loss (Wi-Fi off, airplane mode, etc.)
    window.addEventListener(EBrowserEvents.OFFLINE, handleConnectionLoss);

    return () => {
      SocketService.off(EWebinarSockets.DISCONNECTING, handleConnectionLoss);
      window.removeEventListener(EBrowserEvents.OFFLINE, handleConnectionLoss);
    };
  }, [handleConnectionLoss]);
};

export default useWebinarSocketListener;
